import { Info as InfoIcon } from '@mui/icons-material';
import { Grid, IconButton, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FilterDropdownWithSearch } from 'src/components/filterDropdownWithSearch/FilterDropdownWithSearch';
import { LightTooltip } from 'src/components/settings/settingsCategory/SettingsCategory.styled';
import { InputWidget, SelectDropdownWidget } from 'src/components/widget';
import { FilterDropDownWithBadgesContainer } from 'src/features/components/addComponentPage/AddComponentPage.styled';
import { useGetProductCategories } from 'src/hooks';
import { validateProductCode } from 'src/hooks/api/product/helper';
import { IProduct } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';

import { ProductFormProps } from './ProductForm.interface';
import { StyledGrid } from './ProductForm.styled';

const statusOptions = [
  { value: true, label: 'Active' },
  { value: false, label: 'Inactive' },
];

export const ProductForm = (props: ProductFormProps) => {
  const { formState, control, setValue } = useFormContext<
    IProduct & { asset?: File }
  >();

  const { data: productCategories = [] } = useGetProductCategories([
    'sort=*name',
  ]);

  const { isViewOnly } = useUserRole();

  const onValidate = async (value: string) => {
    const exists = await validateProductCode(value);

    return exists ? 'Product code already exist' : undefined;
  };

  const rawProductCategoryIds = useWatch({
    control,
    name: 'product_category_id',
  });
  const productCategoryIds = useMemo(
    () => rawProductCategoryIds || [],
    [rawProductCategoryIds],
  );

  useEffect(() => {
    const categoryNames: any = productCategoryIds
      .map((v) => productCategories.find((e) => e.id === v)?.name)
      .filter(Boolean);

    if (productCategoryIds.length === categoryNames.length) {
      setValue('product_category_names', categoryNames, {
        shouldDirty: false,
      });
    }
  }, [productCategoryIds, productCategories, setValue]);

  const errors = formState.errors;

  return (
    <StyledGrid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Controller
          name="active"
          control={control}
          render={({ field }) => (
            <SelectDropdownWidget
              {...field}
              label="Group status *"
              selectOptions={statusOptions}
              disabled={isViewOnly}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          rules={
            Boolean(props.productData?.product_code)
              ? {}
              : {
                  required: 'Required',
                  pattern: {
                    value: /^[A-Z0-9-_]{1,255}$/,
                    message: 'Invalid product code',
                  },
                  validate: onValidate,
                }
          }
          name="product_code"
          control={control}
          render={({ field }) => (
            <InputWidget
              {...field}
              label="Group product code *"
              hasError={Boolean(errors.product_code?.message)}
              error={errors.product_code?.message}
              disabled={Boolean(props.productData?.product_code) || isViewOnly}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name="name_override"
          control={control}
          render={({ field }) => (
            <InputWidget
              {...field}
              label={
                <Typography>
                  {lang.productNameOverride}
                  <LightTooltip title="Name override will display at all locations">
                    <IconButton disableRipple>
                      <InfoIcon />
                    </IconButton>
                  </LightTooltip>
                </Typography>
              }
              hasError={Boolean(errors.name_override?.message)}
              error={errors.name_override?.message}
              disabled={isViewOnly}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography>{lang.productCategory}</Typography>
        <Controller
          name="product_category_id"
          control={control}
          render={({ field }) => (
            <FilterDropDownWithBadgesContainer>
              <FilterDropdownWithSearch
                name={lang.productCategory}
                placeholder={lang.addCategoryText}
                multiline={true}
                onChange={(categoryId: any) => {
                  field.onChange(categoryId);
                }}
                value={field.value}
                badgeOptions={{
                  align: 'top',
                }}
                disabled={isViewOnly}
              />
            </FilterDropDownWithBadgesContainer>
          )}
        />
      </Grid>
    </StyledGrid>
  );
};
